import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Edit3, Power, PowerOff, Users, Download } from "lucide-react";
import Layout from "../components/Layout";
import api, { errMessage, tokenStore } from "../lib/api";
import BuyerFormModal from "../components/BuyerFormModal";

async function downloadBuyersCsv() {
  const url = (process.env.REACT_APP_BACKEND_URL || "") + "/api/export/buyers.csv";
  const r = await fetch(url, { headers: { Authorization: `Bearer ${tokenStore.get()}` } });
  if (!r.ok) throw new Error(`${r.status}`);
  const blob = await r.blob();
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = "wholesalehq_buyers.csv";
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(a.href);
}

function summarize(b) {
  const bits = [];
  if (b.states?.length) bits.push(b.states.join("/"));
  if (b.cities?.length) bits.push(b.cities.slice(0, 2).join(", "));
  if (b.min_arv || b.max_arv) {
    const lo = b.min_arv ? `${Math.round(b.min_arv / 1000)}k` : "0";
    const hi = b.max_arv ? `${Math.round(b.max_arv / 1000)}k` : "∞";
    bits.push(`ARV ${lo}–${hi}`);
  }
  if (b.min_beds) bits.push(`${b.min_beds}+ bd`);
  if (b.must_have_signals?.length) bits.push(b.must_have_signals.slice(0, 2).join("+"));
  return bits.join(" · ") || "Any deal";
}

export default function Buyers() {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/buyers");
      setBuyers(r.data || []);
    } catch (e) {
      toast.error(errMessage(e, "Could not load buyers"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onCreate = () => {
    setEditing(null);
    setShowForm(true);
  };

  const onEdit = (b) => {
    setEditing(b);
    setShowForm(true);
  };

  const onSubmit = async (payload) => {
    try {
      if (editing) {
        await api.patch(`/buyers/${editing.id}`, payload);
        toast.success("Buyer updated");
      } else {
        await api.post("/buyers", payload);
        toast.success("Buyer added");
      }
      setShowForm(false);
      setEditing(null);
      load();
    } catch (e) {
      toast.error(errMessage(e, "Could not save buyer"));
    }
  };

  const onDelete = async (b) => {
    if (!window.confirm(`Remove ${b.name}?`)) return;
    try {
      await api.delete(`/buyers/${b.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(errMessage(e, "Could not delete"));
    }
  };

  const toggleActive = async (b) => {
    try {
      await api.patch(`/buyers/${b.id}`, { active: !b.active });
      load();
    } catch (e) {
      toast.error(errMessage(e, "Update failed"));
    }
  };

  return (
    <Layout>
      <div className="px-8 py-10 max-w-5xl">
        <div className="flex items-start justify-between gap-4 mb-8">
          <div>
            <div className="overline mb-3">// Cash buyers</div>
            <h1 className="font-display text-4xl font-bold tracking-tighter">Buyers.</h1>
            <p className="text-sm text-[--ink-muted] mt-2 max-w-xl">
              Build your buy-box Rolodex. When you find a deal, one click matches it
              to every buyer whose criteria fit and blasts an email + SMS in seconds.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <button
              data-testid="export-buyers-csv"
              onClick={async () => {
                try {
                  await downloadBuyersCsv();
                  toast.success("CSV downloaded");
                } catch (err) {
                  toast.error(errMessage(err, "Export failed"));
                }
              }}
              disabled={buyers.length === 0}
              className="btn-ghost flex items-center gap-1.5 text-xs disabled:opacity-40"
            >
              <Download size={12} strokeWidth={1.5} /> Export
            </button>
            <button
              data-testid="add-buyer-button"
              onClick={onCreate}
              className="btn-primary flex items-center gap-2"
            >
              <Plus size={14} strokeWidth={1.5} /> Add buyer
            </button>
          </div>
        </div>

        {loading ? (
          <div className="text-sm text-[--ink-muted] py-12 text-center">Loading…</div>
        ) : buyers.length === 0 ? (
          <div
            data-testid="buyers-empty"
            className="border border-dashed border-[--line] p-12 text-center"
          >
            <Users size={32} strokeWidth={1.25} className="mx-auto mb-3 text-[--ink-muted]" />
            <div className="font-display text-lg font-bold mb-1">No buyers yet</div>
            <div className="text-sm text-[--ink-muted] mb-4">
              Add your first cash buyer and the matching engine will start working.
            </div>
            <button
              data-testid="add-first-buyer"
              onClick={onCreate}
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus size={14} strokeWidth={1.5} /> Add buyer
            </button>
          </div>
        ) : (
          <div className="space-y-2" data-testid="buyers-list">
            {buyers.map((b) => (
              <div
                key={b.id}
                data-testid={`buyer-card-${b.id}`}
                className={`surface-card p-4 flex items-center gap-4 ${
                  !b.active ? "opacity-60" : ""
                }`}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-bold text-base">{b.name}</div>
                    {!b.active && (
                      <span className="text-[10px] px-1.5 py-0.5 bg-[--bg-soft] uppercase tracking-wider font-mono">
                        Paused
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[--ink-muted] font-mono mt-0.5 truncate">
                    {[b.email, b.phone].filter(Boolean).join(" · ") || "—"}
                  </div>
                  <div className="text-xs text-[--ink] mt-1.5 truncate">{summarize(b)}</div>
                </div>
                <div className="flex gap-1.5 shrink-0">
                  <button
                    data-testid={`toggle-buyer-${b.id}`}
                    onClick={() => toggleActive(b)}
                    className="btn-ghost !py-1.5 !px-3 text-xs"
                    title={b.active ? "Pause" : "Activate"}
                  >
                    {b.active ? (
                      <Power size={12} strokeWidth={1.5} />
                    ) : (
                      <PowerOff size={12} strokeWidth={1.5} />
                    )}
                  </button>
                  <button
                    data-testid={`edit-buyer-${b.id}`}
                    onClick={() => onEdit(b)}
                    className="btn-ghost !py-1.5 !px-3 text-xs"
                  >
                    <Edit3 size={12} strokeWidth={1.5} />
                  </button>
                  <button
                    data-testid={`delete-buyer-${b.id}`}
                    onClick={() => onDelete(b)}
                    className="btn-ghost !py-1.5 !px-3 text-xs"
                  >
                    <Trash2 size={12} strokeWidth={1.5} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {showForm && (
        <BuyerFormModal
          buyer={editing}
          onClose={() => {
            setShowForm(false);
            setEditing(null);
          }}
          onSubmit={onSubmit}
        />
      )}
    </Layout>
  );
}
